"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, FilePen, Waypoints } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Evaluación",
  description: "Analizamos a fondo la situación médica, logística y emocional para entender las necesidades reales de tu familiar.",
  icon: Search
}, {
  title: "Plan a Medida",
  description: "Diseñamos una estrategia personalizada con protocolos claros y selección de los mejores cuidadores o servicios.",
  icon: FilePen
}, {
  title: "Coordinación",
  description: "Ejecutamos el plan, supervisamos el día a día y entregamos reportes continuos para tu total tranquilidad.",
  icon: Waypoints
}];
export default function GestionEn3Pasos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="gestión-en-3-pasos" className="relative py-24 md:py-32 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/50 via-background to-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Gestión en 3 Pasos
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Nuestro proceso está diseñado para brindarte claridad y control desde el primer día, actuando como tu apoderado operativo.
          </p>
        </div>

        <div ref={ref} className={`relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="hidden md:block absolute top-[48px] left-[16.66%] right-[16.66%] h-[2px] bg-gradient-to-r from-transparent via-primary/20 to-transparent z-0" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative flex flex-col items-center group">
                  {}
                  <div className="relative z-10 w-24 h-24 flex items-center justify-center mb-8">
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[120px] font-black text-primary/5 select-none transition-transform duration-500 group-hover:scale-110 group-hover:text-primary/10">
                      0{i + 1}
                    </span>
                    <div className="relative z-20 w-16 h-16 bg-card rounded-full shadow-md flex items-center justify-center border border-border/50 transition-colors duration-300 group-hover:border-primary/30 group-hover:bg-accent/20">
                      <Icon className="w-7 h-7 text-primary transition-transform duration-300 group-hover:scale-110" />
                    </div>
                  </div>

                  {}
                  <div className="bg-card rounded-2xl p-8 text-center shadow-sm border border-border/40 w-full flex-1 transition-all duration-500 ease-out group-hover:-translate-y-2 group-hover:shadow-[0_8px_30px_rgba(46,139,154,0.12)] group-hover:border-primary/20">
                    <h3 className="text-xl font-bold text-foreground mb-4">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}